import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { effect, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private darkMode = signal<boolean>(false);
  darkMode$ = this.darkMode.asReadonly();

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      // Initialize the dark mode state
      const savedPreference = localStorage.getItem('darkMode');
      if (savedPreference !== null) {
        this.darkMode.set(savedPreference === 'true');
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        this.darkMode.set(prefersDark);
      }

      effect(() => {
        const isDark = this.darkMode();
        if (isDark) {
          this.document.documentElement.classList.add('dark');
        } else {
          this.document.documentElement.classList.remove('dark');
        }
        // Sync both localStorage and cookie
        localStorage.setItem('darkMode', isDark.toString());
        document.cookie = `darkMode=${isDark}; path=/; max-age=31536000; SameSite=Strict`;
      });
    }
  }

  private isDarkModePrefered(): boolean {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    // Check cookie first for SSR consistency
    const cookieMatch = document.cookie.match(/darkMode=(true|false)/);
    if (cookieMatch) {
      return cookieMatch[1] === 'true';
    }

    // Fall back to localStorage
    const savedPreference = localStorage.getItem('darkMode');
    if (savedPreference !== null) {
      return savedPreference === 'true';
    }

    // Finally, check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  toggleDarkMode() {
    if (isPlatformBrowser(this.platformId)) {
      this.darkMode.update(value => !value);
    }
  }
}
